import { ANYSPEND_MAINNET_BASE_URL } from "../../anyspend/constants/index.js";
import { getNativeToken, isNativeToken } from "../../anyspend/utils/index.js";
import app from "../../global-account/app.js";
import invariant from "invariant";
// Service functions
export const anyspendService = {
    getTokenList: async (chainId, query) => {
        const params = new URLSearchParams({ limit: "100", term: query });
        const response = await fetch(`${ANYSPEND_MAINNET_BASE_URL}/chains/${chainId}/tokens?${params.toString()}`);
        const body = await response.json();
        invariant(response.status === 200, `Failed to fetch token list for chain ${chainId}`);
        return body.data;
    },
    getToken: async (chainId, tokenAddress) => {
        if (isNativeToken(tokenAddress)) {
            return getNativeToken(chainId);
        }
        const tokenList = await anyspendService.getTokenList(chainId, tokenAddress);
        const token = tokenList.find((t) => t.address.toLowerCase() === tokenAddress.toLowerCase());
        if (!token) {
            throw new Error(`Token ${tokenAddress} not found on chain ${chainId}`);
        }
        return token;
    },
    getQuote: async (req, partnerId) => {
        const url = `${ANYSPEND_MAINNET_BASE_URL}/orders/quote`;
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ ...req, partnerId }),
        });
        const data = await response.json();
        if (response.status !== 200)
            throw new Error(data.message);
        return data;
    },
    // Order related
    createOrder: async ({ recipientAddress, type, srcChain, dstChain, srcTokenAddress, dstTokenAddress, srcAmount, payload, onramp, metadata, creatorAddress, partnerId, clientReferenceId, visitorData, callbackMetadata, feeOnTop, kycWalletHeaders, }) => {
        const accessToken = await app.authentication.getAccessToken();
        const response = await fetch(`${ANYSPEND_MAINNET_BASE_URL}/orders`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...(visitorData?.requestId && { "X-Fingerprint-Request-Id": visitorData.requestId }),
                ...(visitorData?.visitorId && { "X-Fingerprint-Visitor-Id": visitorData.visitorId }),
                ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
                ...kycWalletHeaders,
            },
            body: JSON.stringify({
                recipientAddress,
                type,
                srcChain,
                dstChain,
                srcTokenAddress,
                dstTokenAddress,
                srcAmount,
                payload,
                onramp,
                metadata,
                creatorAddress,
                partnerId,
                ...(clientReferenceId && { clientReferenceId }),
                ...(callbackMetadata && { callbackMetadata }),
                ...(feeOnTop != null && { feeOnTop }),
            }),
        });
        const data = await response.json();
        invariant(response.status === 200, data.message);
        return data;
    },
    getOrderAndTransactions: async (orderId) => {
        invariant(orderId, "orderId is required");
        const response = await fetch(`${ANYSPEND_MAINNET_BASE_URL}/orders/${orderId}`);
        const data = await response.json();
        invariant(response.status === 200, data.message || `Failed to fetch order ${orderId}`);
        return data;
    },
    getOrderHistory: async (creatorAddress, limit = 100, offset = 0) => {
        const params = new URLSearchParams({
            limit: limit.toString(),
            offset: offset.toString(),
        });
        if (creatorAddress) {
            params.append("creatorAddress", creatorAddress);
        }
        const response = await fetch(`${ANYSPEND_MAINNET_BASE_URL}/orders?${params.toString()}`);
        const data = await response.json();
        invariant(response.status === 200, data.message || "Failed to fetch order history");
        return data;
    },
    getCoinbaseOnrampOptions: async (country, visitorData) => {
        invariant(country, "country is required");
        const params = new URLSearchParams({
            country,
            // include fingerprintId and requestId in the query params
            ...(visitorData?.requestId && { requestId: visitorData.requestId }),
            ...(visitorData?.visitorId && { fingerprintId: visitorData.visitorId }),
        });
        const response = await fetch(`${ANYSPEND_MAINNET_BASE_URL}/onramp/coinbase/options?${params.toString()}`);
        const data = await response.json();
        return data;
    },
    checkStripeSupport: async (usdAmount, visitorData) => {
        const params = new URLSearchParams({
            usdAmount: usdAmount || "",
        });
        const response = await fetch(`${ANYSPEND_MAINNET_BASE_URL}/onramp/stripe/supported?${params.toString()}`, {
            headers: {
                ...(visitorData?.requestId && { "X-Fingerprint-Request-Id": visitorData.requestId }),
                ...(visitorData?.visitorId && { "X-Fingerprint-Visitor-Id": visitorData.visitorId }),
            },
        });
        const data = await response.json();
        invariant(response.status === 200, "Failed to check Stripe support");
        return data.data;
    },
    getCoinbaseOnrampUrl: async (request) => {
        const response = await fetch(`${ANYSPEND_MAINNET_BASE_URL}/onramp/coinbase/onramp-url`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(request),
        });
        const data = await response.json();
        if (!response.ok)
            throw new Error(data.message || "Failed to get Coinbase onramp URL");
        return data;
    },
    getStripeClientSecret: async (paymentIntentId) => {
        const params = new URLSearchParams({ paymentIntentId });
        const response = await fetch(`${ANYSPEND_MAINNET_BASE_URL}/stripe/clientSecret?${params.toString()}`);
        const data = await response.json();
        invariant(response.status === 200, "Failed to get Stripe client secret");
        return data.data;
    },
};
